import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertCircle, Activity } from "lucide-react";

interface NodeCard {
  key: string;
  label: string;
  table: string;
  count24h: number;
  status: "live" | "stale" | "off";
}

const NODES: Array<{ key: string; label: string; table: string }> = [
  { key: "input_gating", label: "Input Gating", table: "input_gating_decisions" },
  { key: "threat", label: "Threat Detect", table: "threat_detections" },
  { key: "conflict", label: "Conflict Detect", table: "conflict_detections" },
  { key: "tom", label: "ToM Models", table: "tom_models" },
  { key: "affect", label: "Affect States", table: "affect_states" },
  { key: "group_state", label: "Group State", table: "group_state_classifications" },
  { key: "metacog", label: "Metacognition", table: "metacog_runtime_decisions" },
  { key: "inhibitor", label: "Inhibitor", table: "inhibitor_decisions" },
  { key: "pattern_sep", label: "Pattern Separation", table: "pattern_separation_runs" },
];

export default function CortexLiveCards() {
  const [cards, setCards] = useState<NodeCard[]>([]);
  const [pipelineRunsHr, setPipelineRunsHr] = useState<number>(0);
  const [applyLog7d, setApplyLog7d] = useState<number>(0);
  const [quarantine7d, setQuarantine7d] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const since = new Date(Date.now() - 24 * 3600 * 1000).toISOString();
      const results: NodeCard[] = [];
      for (const n of NODES) {
        try {
          const { count } = await supabase
            .from(n.table as never)
            .select("*", { count: "exact", head: true })
            .gte("created_at", since);
          const c = count ?? 0;
          results.push({
            ...n,
            count24h: c,
            status: c > 0 ? "live" : "off",
          });
        } catch {
          results.push({ ...n, count24h: 0, status: "off" });
        }
      }
      setCards(results);

      const hourAgo = new Date(Date.now() - 3600 * 1000).toISOString();
      const week = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
      const day = new Date(Date.now() - 24 * 3600 * 1000).toISOString();

      const [pr, al, qm] = await Promise.all([
        supabase.from("pipeline_runs").select("*", { count: "exact", head: true }).gte("created_at", hourAgo),
        supabase.from("apply_log").select("*", { count: "exact", head: true }).gte("created_at", week),
        supabase.from("quarantined_memories").select("*", { count: "exact", head: true }).gte("created_at", day),
      ]);
      setPipelineRunsHr(pr.count ?? 0);
      setApplyLog7d(al.count ?? 0);
      setQuarantine7d(qm.count ?? 0);
      setLoading(false);
    };
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Cortex — Nó vivo? (24h)</h1>
        <p className="text-muted-foreground">
          Cada card mostra a atividade real medida em produção. Verde = nó ativo, cinza = sem registros nas últimas 24h.
        </p>
      </div>

      {/* Gates globais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 flex items-center gap-3">
          <Activity className={pipelineRunsHr >= 30 ? "text-emerald-500" : "text-amber-500"} />
          <div>
            <div className="text-sm text-muted-foreground">pipeline_runs (1h)</div>
            <div className="text-2xl font-bold">{pipelineRunsHr}</div>
            <div className="text-xs text-muted-foreground">Gate G2: ≥30</div>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <CheckCircle2 className={applyLog7d > 0 ? "text-emerald-500" : "text-amber-500"} />
          <div>
            <div className="text-sm text-muted-foreground">apply_log (7d)</div>
            <div className="text-2xl font-bold">{applyLog7d}</div>
            <div className="text-xs text-muted-foreground">Gate G4: &gt;0</div>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <CheckCircle2 className={quarantine7d > 0 ? "text-emerald-500" : "text-muted-foreground"} />
          <div>
            <div className="text-sm text-muted-foreground">quarantined_memories (24h)</div>
            <div className="text-2xl font-bold">{quarantine7d}</div>
            <div className="text-xs text-muted-foreground">Gate G5: &gt;0/dia</div>
          </div>
        </Card>
      </div>

      {/* 9 cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {loading
          ? Array.from({ length: 9 }).map((_, i) => <Card key={i} className="h-32 animate-pulse" />)
          : cards.map((c) => (
              <Card key={c.key} className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-semibold">{c.label}</div>
                    <div className="text-xs text-muted-foreground">{c.table}</div>
                  </div>
                  {c.status === "live" ? (
                    <Badge className="bg-primary/15 text-primary border-primary/30">Vivo</Badge>
                  ) : (
                    <Badge variant="secondary" className="bg-muted text-muted-foreground">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Sem dados
                    </Badge>
                  )}
                </div>
                <div className="text-3xl font-bold mt-2">{c.count24h}</div>
                <div className="text-xs text-muted-foreground">registros nas últimas 24h</div>
              </Card>
            ))}
      </div>
    </div>
  );
}
